import { useMemo, useState, useEffect } from "react";
import Api from "../Services/Api";
import FormularioList from "./FormularioList";
import FormularioInput from "./FormularioInput";
import "./FormularioPedidoOmie.css";

function toFiniteNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function pickFirstNumber(...values) {
  for (const value of values) {
    const n = toFiniteNumber(value);
    if (n !== null) return n;
  }
  return null;
}

function toNumber(value, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) ? n : fallback;
}

function pickFirst(...values) {
  for (const value of values) {
    if (value !== undefined && value !== null && String(value).trim() !== "") return value;
  }
  return null;
}

function parseJson(raw) {
  if (!raw) return null;
  if (typeof raw === "object") return raw;
  if (typeof raw !== "string") return null;
  try {
    return JSON.parse(raw);
  } catch (_err) {
    return null;
  }
}

function readPath(obj, path) {
  if (!obj || typeof obj !== "object" || !path) return null;
  const value = String(path)
    .split(".")
    .reduce((acc, key) => (acc && acc[key] !== undefined ? acc[key] : undefined), obj);
  return value === undefined ? null : value;
}

function extractRawOrder(rawInput) {
  const parsed = parseJson(rawInput);
  if (!parsed) return null;

  if (parsed.pedido_venda_produto) return parsed.pedido_venda_produto;
  if (parsed.PedidoVendaProduto) return parsed.PedidoVendaProduto;
  return parsed;
}

function extractOrderValues(rawInput) {
  const raw = extractRawOrder(rawInput);
  if (!raw || typeof raw !== "object") {
    return {
      total_value: null,
      freight_value: null,
      products_value: null,
    };
  }

  const totals = raw.total_pedido || raw.TotalPedido || raw.totais || {};
  const freightBlock = raw.frete || raw.Frete || {};
  const discountBlock = raw.desconto || raw.Desconto || {};

  const total_value = pickFirstNumber(
    totals.valor_total_pedido,
    raw.valor_total_pedido,
    raw.total,
    totals.valor_total
  );

  let freight_value = pickFirstNumber(
    totals.valor_frete,
    totals.frete,
    totals.total_frete,
    raw.valor_frete,
    raw.total_frete,
    freightBlock.valor_frete,
    freightBlock.frete,
    freightBlock.total_frete,
    readPath(raw, "transporte.valor_frete"),
    readPath(raw, "Transporte.valor_frete")
  );

  const products_value = pickFirstNumber(
    totals.valor_mercadorias,
    totals.total_mercadorias,
    totals.valor_total_mercadorias,
    totals.total_produtos,
    totals.valor_total_produtos,
    totals.subtotal,
    raw.valor_mercadorias,
    raw.total_mercadorias,
    raw.valor_total_mercadorias,
    raw.total_produtos,
    raw.valor_total_produtos,
    raw.subtotal
  );

  let discount_value = pickFirstNumber(
    totals.valor_desconto,
    totals.valor_descontos,
    totals.desconto,
    totals.total_desconto,
    totals.desc_total,
    totals.valor_desc,
    raw.valor_desconto,
    raw.valor_descontos,
    raw.total_desconto,
    raw.desc_total,
    raw.valor_desc,
    discountBlock.valor_desconto,
    discountBlock.valor_descontos,
    discountBlock.desconto,
    discountBlock.valor,
    readPath(raw, "total_pedido.valor_desconto"),
    readPath(raw, "total_pedido.valor_descontos"),
    readPath(raw, "TotalPedido.valor_desconto"),
    readPath(raw, "TotalPedido.valor_descontos")
  );

  if (discount_value === null && total_value !== null && products_value !== null && freight_value !== null) {
    const inferredDiscount = Number((products_value + freight_value - total_value).toFixed(2));
    if (Number.isFinite(inferredDiscount) && inferredDiscount > 0) {
      discount_value = inferredDiscount;
    }
  }

  let products_net_value = null;
  if (products_value !== null && discount_value !== null) {
    products_net_value = Number((products_value - discount_value).toFixed(2));
  } else if (total_value !== null && freight_value !== null) {
    products_net_value = Number((total_value - freight_value).toFixed(2));
  } else if (products_value !== null) {
    products_net_value = Number(products_value.toFixed(2));
  }

  if (freight_value === null && total_value !== null && products_net_value !== null) {
    freight_value = Number((total_value - products_net_value).toFixed(2));
  }

  if (products_net_value !== null && products_net_value < 0) products_net_value = 0;
  if (freight_value !== null && freight_value < 0) freight_value = 0;
  if (discount_value !== null && discount_value < 0) discount_value = 0;

  return { total_value, freight_value, products_value, discount_value, products_net_value };
}

function extractOrderItems(rawInput) {
  const raw = extractRawOrder(rawInput);
  if (!raw || typeof raw !== "object") return [];

  const candidates = [
    raw.det,
    raw.DET,
    raw.itens,
    raw.Itens,
    raw.items,
    raw.produtos,
    raw.lista_itens,
    raw.pedido_itens,
  ];

  const baseItems = candidates.find((candidate) => Array.isArray(candidate)) || [];

  return baseItems
    .map((item, index) => {
      if (!item || typeof item !== "object") return null;

      const produto = item.produto || item.Produto || {};
      const ide = item.ide || item.Ide || {};

      const quantity = toNumber(
        pickFirst(produto.quantidade, item.quantidade, produto.qtde, item.qtde, 1),
        1
      );
      const unit_price = toNumber(
        pickFirst(produto.valor_unitario, item.valor_unitario, produto.preco_unitario, item.preco_unitario),
        0
      );

      const gross_total = pickFirstNumber(
        produto.valor_mercadoria,
        item.valor_mercadoria,
        produto.valor_total_bruto,
        item.valor_total_bruto
      );
      const item_discount = pickFirstNumber(
        produto.valor_desconto,
        item.valor_desconto,
        produto.desconto,
        item.desconto
      );

      let item_total = pickFirstNumber(
        produto.valor_total,
        item.valor_total,
        produto.subtotal,
        item.subtotal
      );
      if (item_total === null && gross_total !== null && item_discount !== null) {
        item_total = Number((gross_total - item_discount).toFixed(2));
      }
      if (item_total === null) {
        item_total = Number((quantity * unit_price).toFixed(2));
      }

      const description = String(
        pickFirst(
          produto.descricao,
          produto.descricao_produto,
          item.descricao,
          item.nome,
          `Item ${index + 1}`
        )
      ).trim();

      const item_code = String(
        pickFirst(
          produto.codigo_produto,
          item.codigo_produto,
          ide.codigo_item,
          item.codigo_item,
          item.id,
          `ITEM_${index + 1}`
        )
      ).trim();

      return {
        row_key: `${item_code}_${index}`,
        item_code,
        description,
        quantity,
        unit_price,
        item_total: toNumber(item_total, 0),
        commission_percent: "",
      };
    })
    .filter(Boolean);
}

function extractOrderInstallments(rawInput) {
  const raw = extractRawOrder(rawInput);
  if (!raw || typeof raw !== "object") return [];

  const cabecalho = raw.cabecalho || raw.Cabecalho || {};
  const blocoParcelas = raw.lista_parcelas || raw.ListaParcelas || raw.parcelas || raw.Parcelas || {};
  const parcelasRaw = pickFirst(
    blocoParcelas.parcela,
    blocoParcelas.Parcela,
    raw.parcela,
    raw.parcelas
  );

  let parcelas = (Array.isArray(parcelasRaw) ? parcelasRaw : parcelasRaw ? [parcelasRaw] : [])
    .filter((item) => item && typeof item === "object")
    .map((item, index) => {
      const numeroParcela = Number(
        pickFirst(item.numero_parcela, item.parcela, item.numero, index + 1)
      );

      return {
        numero_parcela: Number.isFinite(numeroParcela) && numeroParcela > 0 ? numeroParcela : index + 1,
        valor: toFiniteNumber(pickFirst(item.valor, item.valor_parcela, item.valor_documento)),
        percentual: toFiniteNumber(pickFirst(item.percentual, item.percentual_parcela)),
        data_vencimento: String(pickFirst(item.data_vencimento, item.vencimento, item.data) || "").trim(),
        tipo_documento: String(item.tipo_documento || "").trim().toUpperCase(),
        meio_pagamento: String(item.meio_pagamento || "").trim().toUpperCase(),
      };
    });

  const qtdCabecalho = Number(toFiniteNumber(cabecalho?.qtde_parcelas) || 0);
  if (parcelas.length === 0 && qtdCabecalho > 0) {
    parcelas = Array.from({ length: qtdCabecalho }).map((_, index) => ({
      numero_parcela: index + 1,
      valor: null,
      percentual: null,
      data_vencimento: "",
      tipo_documento: "",
      meio_pagamento: "",
    }));
  }

  return parcelas.sort((a, b) => a.numero_parcela - b.numero_parcela);
}

function extractOrderPaymentInfo(rawInput) {
  const raw = extractRawOrder(rawInput);
  const cabecalho = raw?.cabecalho || raw?.Cabecalho || {};
  const installments = extractOrderInstallments(rawInput);

  const codigoParcela = String(
    pickFirst(cabecalho?.codigo_parcela, cabecalho?.CodigoParcela, "")
  )
    .trim()
    .toUpperCase();

  const isBoletoByHeader = codigoParcela.includes("BOL");
  const isBoletoByInstallment = installments.some(
    (item) => item.tipo_documento === "BOL" || item.meio_pagamento === "15"
  );

  const count = installments.length || Number(toFiniteNumber(cabecalho?.qtde_parcelas) || 1);
  const isBoleto = isBoletoByHeader || isBoletoByInstallment;

  let paymentTypeLabel = "A vista";
  if (isBoleto) paymentTypeLabel = "Boleto";
  else if (count > 1) paymentTypeLabel = "Parcelado";

  return {
    isBoleto,
    installmentsCount: count > 0 ? count : 1,
    paymentTypeLabel,
    installmentsLabel: `${count > 0 ? count : 1}x`,
  };
}

function parseSavedCommissionItems(rawInput) {
  if (!rawInput) return [];

  let parsed = rawInput;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch (_err) {
      return [];
    }
  }

  if (!Array.isArray(parsed)) return [];

  return parsed
    .map((item, index) => ({
      row_key: `${String(item?.item_code || "ITEM")}_${index}`,
      item_code: String(item?.item_code || `ITEM_${index + 1}`),
      description: String(item?.description || `Item ${index + 1}`),
      quantity: toNumber(item?.quantity, 0),
      unit_price: toNumber(item?.unit_price, 0),
      item_total: toNumber(item?.item_total, 0),
      commission_percent:
        item?.commission_percent === null || item?.commission_percent === undefined
          ? ""
          : String(item.commission_percent),
    }))
    .filter((item) => item.item_total > 0 || item.commission_percent !== "");
}

function mergeItems(orderItems, savedItems, defaultPercent) {
  const defaultPercentStr =
    defaultPercent === null || defaultPercent === undefined ? "" : String(defaultPercent);

  if (orderItems.length === 0 && savedItems.length > 0) {
    return savedItems.map((item) => ({
      ...item,
      commission_percent:
        item.commission_percent === "" ? defaultPercentStr : String(item.commission_percent),
    }));
  }

  return orderItems.map((item, index) => {
    const byCode = savedItems.find((saved) => String(saved.item_code) === String(item.item_code));
    const byIndex = savedItems[index];
    const saved = byCode || byIndex || null;

    return {
      ...item,
      commission_percent:
        saved?.commission_percent !== undefined && saved?.commission_percent !== null
          ? String(saved.commission_percent)
          : defaultPercentStr,
    };
  });
}

function resolvePercent(itemPercent, generalPercent) {
  const item = toFiniteNumber(itemPercent);
  if (item !== null) return item;
  return generalPercent;
}

function calcCommissionValue(itemTotal, percent) {
  const total = toNumber(itemTotal, 0);
  const pct = toNumber(percent, 0);
  return Number((total * (pct / 100)).toFixed(2));
}

function rebalanceItemTotals(items, targetTotalInput) {
  if (!Array.isArray(items) || items.length === 0) return items;

  const targetTotal = toFiniteNumber(targetTotalInput);
  if (targetTotal === null || targetTotal <= 0) return items;

  const currentTotal = Number(
    items.reduce((acc, item) => acc + toNumber(item.item_total, 0), 0).toFixed(2)
  );

  if (currentTotal <= 0) return items;
  if (Math.abs(currentTotal - targetTotal) < 0.01) return items;

  let acumulado = 0;
  return items.map((item, index) => {
    if (index === items.length - 1) {
      const ultimo = Number((targetTotal - acumulado).toFixed(2));
      return { ...item, item_total: ultimo >= 0 ? ultimo : 0 };
    }

    const proporcional = Number(
      ((toNumber(item.item_total, 0) * targetTotal) / currentTotal).toFixed(2)
    );
    acumulado += proporcional;
    return { ...item, item_total: proporcional };
  });
}

function formatCurrencyBRL(value) {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "R$ 0,00";
  return amount.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function normalizeVendorCode(value) {
  const text = String(value || "").trim();
  if (!text) return "";
  return text.replace(/\D/g, "");
}

const FormularioPedidoOmie = ({ licenciados = [], pedido, onClose, onReload }) => {
  const [loading, setLoading] = useState(false);
  const [licensed_id, setLicensedId] = useState("");
  const [commission_percent, setCommissionPercent] = useState("");
  const [status, setStatus] = useState("PENDING");
  const [observacao, setObservacao] = useState("");
  const [commissionItems, setCommissionItems] = useState([]);

  const parsedOrderItems = useMemo(() => {
    return extractOrderItems(pedido?.raw_json);
  }, [pedido?.raw_json]);

  const paymentInfo = useMemo(() => {
    return extractOrderPaymentInfo(pedido?.raw_json);
  }, [pedido?.raw_json]);

  const valoresPedido = useMemo(() => {
    const fromRaw = extractOrderValues(pedido?.raw_json);
    const savedItems = parseSavedCommissionItems(pedido?.commission_items_json);
    const itensBase = parsedOrderItems.length > 0 ? parsedOrderItems : savedItems;
    const valorProdutosBruto = toFiniteNumber(fromRaw.products_value);
    const valorDescontoExplicito = toFiniteNumber(fromRaw.discount_value);
    const valorDescontoSalvo = toFiniteNumber(pedido?.valor_desconto);

    const valorProdutosExplicito = toFiniteNumber(
      pickFirst(
        fromRaw.products_net_value,
        pedido?.valor_produtos,
        fromRaw.products_value
      )
    );

    const valorFreteExplicito = toFiniteNumber(
      pickFirst(
        fromRaw.freight_value,
        pedido?.valor_frete
      )
    );

    const totalPedidoRaw = toFiniteNumber(
      pickFirst(
        pedido?.valor_total_pedido,
        fromRaw.total_value,
        pedido?.valor_compra
      )
    );

    const somaItens =
      itensBase.length > 0
        ? Number(
            itensBase
              .reduce((acc, item) => acc + toNumber(item.item_total, 0), 0)
              .toFixed(2)
          )
        : null;

    const valorCompraSemFrete = (() => {
      if (valorProdutosExplicito !== null) return valorProdutosExplicito;
      if (totalPedidoRaw !== null && valorFreteExplicito !== null) {
        return Number((totalPedidoRaw - valorFreteExplicito).toFixed(2));
      }
      if (somaItens !== null) return somaItens;
      return toNumber(totalPedidoRaw, 0);
    })();

    const valorFrete = (() => {
      if (valorFreteExplicito !== null) return valorFreteExplicito;
      if (totalPedidoRaw !== null) {
        return Number((totalPedidoRaw - valorCompraSemFrete).toFixed(2));
      }
      return 0;
    })();

    const totalPedido =
      totalPedidoRaw !== null
        ? totalPedidoRaw
        : Number((valorCompraSemFrete + valorFrete).toFixed(2));

    const valorDesconto = (() => {
      if (valorDescontoSalvo !== null) return valorDescontoSalvo;
      if (valorDescontoExplicito !== null) return valorDescontoExplicito;
      if (valorProdutosBruto !== null) {
        return Number(Math.max(0, valorProdutosBruto - valorCompraSemFrete).toFixed(2));
      }
      return 0;
    })();

    return {
      valorCompra: Number(Math.max(0, valorCompraSemFrete).toFixed(2)),
      valorDesconto: Number(Math.max(0, valorDesconto).toFixed(2)),
      valorFrete: Number(Math.max(0, valorFrete).toFixed(2)),
      totalPedido: Number(Math.max(0, totalPedido).toFixed(2)),
    };
  }, [
    parsedOrderItems,
    pedido?.commission_items_json,
    pedido?.valor_desconto,
    pedido?.valor_frete,
    pedido?.valor_produtos,
    pedido?.raw_json,
    pedido?.valor_compra,
    pedido?.valor_total_pedido,
  ]);

  const valorCompra = valoresPedido.valorCompra;

  useEffect(() => {
    if (!pedido) return;

    setLicensedId(pedido.licenciado_id ? String(pedido.licenciado_id) : "");
    setCommissionPercent(pedido.percentual == null ? "" : String(pedido.percentual));
    const statusPedido = String(pedido.status || "PENDING").trim().toUpperCase();
    setStatus(statusPedido === "CANCELLED" ? "CANCELED" : statusPedido);
    setObservacao(pedido.observacao == null ? "" : String(pedido.observacao));

    const savedItems = parseSavedCommissionItems(pedido.commission_items_json);
    const mergedItems = mergeItems(parsedOrderItems, savedItems, pedido.percentual);
    const rawValues = extractOrderValues(pedido.raw_json);
    const explicitProducts = toFiniteNumber(
      pickFirst(
        rawValues.products_net_value,
        pedido?.valor_produtos,
        rawValues.products_value
      )
    );
    const explicitTotal = toFiniteNumber(
      pickFirst(
        pedido?.valor_total_pedido,
        rawValues.total_value,
        pedido?.valor_compra
      )
    );
    const explicitFreight = toFiniteNumber(
      pickFirst(
        rawValues.freight_value,
        pedido?.valor_frete
      )
    );
    const targetProducts =
      explicitProducts !== null
        ? explicitProducts
        : (explicitTotal !== null && explicitFreight !== null
            ? Number((explicitTotal - explicitFreight).toFixed(2))
            : null);

    setCommissionItems(rebalanceItemTotals(mergedItems, targetProducts));
  }, [pedido, parsedOrderItems]);

  const listaLicenciados = useMemo(() => {
    return (licenciados || []).map((licenciado) => ({
      label: licenciado.name,
      value: String(licenciado.id),
    }));
  }, [licenciados]);

  const licenciadoTravadoPorVendedor = useMemo(() => {
    if (!licensed_id || !pedido?.omie_vendor_code) return null;

    const vendorCodePedido = normalizeVendorCode(pedido.omie_vendor_code);
    if (!vendorCodePedido) return null;

    return (
      (licenciados || []).find((licenciado) => {
        if (String(licenciado.id) !== String(licensed_id)) return false;
        return normalizeVendorCode(licenciado.omie_vendor_code) === vendorCodePedido;
      }) || null
    );
  }, [licenciados, licensed_id, pedido?.omie_vendor_code]);

  const licenciadoBloqueado = Boolean(licenciadoTravadoPorVendedor);

  const listaStatus = useMemo(
    () => [
      { label: "Pendente", value: "PENDING" },
      { label: "Aguardando confirmacao", value: "AWAITING_CONFIRMATION" },
      { label: "Confirmado", value: "CONFIRMED" },
      { label: "Cancelado", value: "CANCELED" },
    ],
    []
  );

  const valorPrevisto = useMemo(() => {
    const geral = toFiniteNumber(commission_percent);

    if ((commissionItems || []).length === 0) {
      if (geral === null || geral < 0) return 0;
      return calcCommissionValue(valorCompra, geral);
    }

    return Number(
      commissionItems
        .reduce((acc, item) => {
          const pct = resolvePercent(item.commission_percent, geral);
          if (pct === null || pct < 0) return acc;
          return acc + calcCommissionValue(item.item_total, pct);
        }, 0)
        .toFixed(2)
    );
  }, [commissionItems, commission_percent, valorCompra]);

  const licensedFieldHelper = useMemo(() => {
    if (licenciadoTravadoPorVendedor) {
      return `Licenciado preenchido automaticamente pelo vendedor Omie ${pedido.omie_vendor_code}.`;
    }
    if (pedido?.omie_vendor_code && licensed_id) {
      return `Pedido com vendedor Omie ${pedido.omie_vendor_code}, mas o licenciado ainda pode ser ajustado manualmente.`;
    }
    if (pedido?.omie_vendor_code) {
      return `Codigo vendedor Omie detectado: ${pedido.omie_vendor_code}. Vincule um licenciado para automatizar os proximos pedidos.`;
    }
    return "Selecione o licenciado";
  }, [licenciadoTravadoPorVendedor, licensed_id, pedido?.omie_vendor_code]);

  const buildCommissionPayload = () => {
    const geral = toFiniteNumber(commission_percent);
    if (commission_percent !== "" && (geral === null || geral < 0)) {
      return { error: "Informe um % geral de comissao valido." };
    }

    if ((commissionItems || []).length === 0) {
      if (geral === null || geral <= 0) {
        return { error: "Informe um % de comissao valido." };
      }

      return {
        generalPercent: geral,
        commissionItemsPayload: [],
      };
    }

    const payloadItems = [];
    let totalCommission = 0;

    for (const item of commissionItems) {
      const pct = resolvePercent(item.commission_percent, geral);
      if (pct === null || !Number.isFinite(pct) || pct < 0) {
        return {
          error: `Informe um % de comissao valido para o produto "${item.description}".`,
        };
      }

      const commissionValue = calcCommissionValue(item.item_total, pct);
      totalCommission += commissionValue;

      payloadItems.push({
        item_code: item.item_code,
        description: item.description,
        quantity: toNumber(item.quantity, 0),
        unit_price: toNumber(item.unit_price, 0),
        item_total: toNumber(item.item_total, 0),
        commission_percent: pct,
        commission_value: commissionValue,
      });
    }

    if (Number(totalCommission.toFixed(2)) <= 0) {
      return { error: "A comissao total precisa ser maior que zero." };
    }

    return {
      generalPercent: geral,
      commissionItemsPayload: payloadItems,
    };
  };

  const validar = () => {
    if (!licensed_id) return "Selecione o licenciado.";
    if (!status) return "Selecione o status.";

    const { error } = buildCommissionPayload();
    if (error) return error;

    return null;
  };

  const aplicarPercentualGeral = (value) => {
    setCommissionPercent(value);
    if (!commissionItems.length) return;

    setCommissionItems((prev) =>
      prev.map((item) => ({
        ...item,
        commission_percent: value,
      }))
    );
  };

  const alterarPercentualItem = (rowKey, value) => {
    setCommissionItems((prev) =>
      prev.map((item) =>
        item.row_key === rowKey
          ? {
              ...item,
              commission_percent: value,
            }
          : item
      )
    );
  };

  const confirmarEGerar = async (event) => {
    if (event) event.preventDefault();

    const erro = validar();
    if (erro) return alert(erro);

    const commissionPayload = buildCommissionPayload();
    if (commissionPayload.error) return alert(commissionPayload.error);

    try {
      setLoading(true);

      const observacaoNormalizada = String(observacao || "").trim();
      const patchPayload = {
        licensed_id: Number(licensed_id),
        commission_percent: commissionPayload.generalPercent,
        status,
      };

      if (observacaoNormalizada !== "" || String(pedido?.observacao || "").trim() !== "") {
        patchPayload.observacao = observacaoNormalizada || null;
      }

      if ((commissionPayload.commissionItemsPayload || []).length > 0) {
        patchPayload.commission_items = commissionPayload.commissionItemsPayload;
      }

      await Api.patch(`/omie/orders/${pedido.id}`, patchPayload);

      await Api.post(`/omie/orders/${pedido.id}/confirm`, {
        ledger_status: status,
        split_installments: paymentInfo.isBoleto && paymentInfo.installmentsCount > 1,
      });

      onReload?.();
      onClose?.();
    } catch (err) {
      alert(err?.response?.data?.message || "Erro ao confirmar e gerar lancamento.");
    } finally {
      setLoading(false);
    }
  };

  if (!pedido) return null;

  return (
    <section className="card-comissao">
      <div className="card-header-comissao">
        <div>
          <div className="card-title">Pedido Omie</div>
          <div className="card-subtitle">
            Vincule ao licenciado e confirme para gerar o lancamento no extrato.
          </div>
        </div>
      </div>

      <form className="lancamentoForm" noValidate onSubmit={confirmarEGerar}>
        <FormularioInput
          type="text"
          size="quarto"
          title="Cliente"
          id="omie_cliente"
          valor={pedido.cliente || ""}
          readOnly
          aoAlterado={() => {}}
        />

        <FormularioInput
          type="text"
          size="quarto"
          title="N do pedido"
          id="omie_numero"
          valor={pedido.numero || ""}
          readOnly
          aoAlterado={() => {}}
        />

        <FormularioInput
          type="text"
          size="quarto"
          title="Pagamento"
          id="omie_pagamento_tipo"
          valor={paymentInfo.paymentTypeLabel}
          readOnly
          aoAlterado={() => {}}
        />

        <FormularioInput
          type="text"
          size="quarto"
          title="Parcelas"
          id="omie_parcelas"
          valor={paymentInfo.installmentsLabel}
          helper={
            paymentInfo.isBoleto
              ? `Boleto detectado na Omie (${paymentInfo.installmentsLabel})`
              : "Forma de pagamento identificada no pedido"
          }
          readOnly
          aoAlterado={() => {}}
        />

        <FormularioInput
          type="text"
          size="quarto"
          title="Vendedor Omie"
          id="omie_vendor_code"
          valor={pedido.omie_vendor_code || ""}
          helper={
            pedido.omie_vendor_code
              ? "Codigo do vendedor recebido da Omie."
              : "Este pedido nao trouxe vendedor da Omie."
          }
          readOnly
          aoAlterado={() => {}}
        />

        <FormularioInput
          type="number"
          size="quarto"
          title="Valor da compra (R$)"
          id="omie_valor"
          valor={String(valoresPedido.valorCompra)}
          readOnly
          aoAlterado={() => {}}
        />

        <FormularioInput
          type="number"
          size="quarto"
          title="Frete (R$)"
          id="omie_frete"
          valor={String(valoresPedido.valorFrete)}
          readOnly
          aoAlterado={() => {}}
        />

        <FormularioInput
          type="number"
          size="quarto"
          title="Desconto (R$)"
          id="omie_desconto"
          valor={String(valoresPedido.valorDesconto)}
          readOnly
          aoAlterado={() => {}}
        />

        <FormularioInput
          type="number"
          size="quarto"
          title="Total do pedido (R$)"
          id="omie_total_pedido"
          valor={String(valoresPedido.totalPedido)}
          readOnly
          aoAlterado={() => {}}
        />

        {paymentInfo.isBoleto && paymentInfo.installmentsCount > 1 && (
          <div className="form-group" style={{
            gridColumn: "1 / -1",
            marginBottom: 12,
            background: "#fff7ed",
            border: "1px solid #fed7aa",
            borderRadius: 8,
            padding: "10px 12px",
            fontSize: "0.78rem",
            color: "#9a3412",
          }}>
            Ao confirmar, sera gerado 1 lancamento por parcela.
          </div>
        )}

        <FormularioList
          itens={listaLicenciados}
          title="Licenciado"
          required="required"
          id="licensed_id"
          helper={licensedFieldHelper}
          valor={licensed_id}
          aoAlterado={(value) => setLicensedId(value)}
          disabled={licenciadoBloqueado}
        />

        <FormularioInput
          type="number"
          size="quarto"
          title="Percentual geral (%)"
          id="commission_percent"
          placeholder="Ex: 18"
          helper={`Comissao prevista total: ${formatCurrencyBRL(valorPrevisto)}. Se preencher, aplica em todos os produtos.`}
          valor={commission_percent}
          aoAlterado={aplicarPercentualGeral}
        />

        <FormularioList
          itens={listaStatus}
          title="Status"
          required="required"
          id="status"
          helper="Defina o status do pedido"
          valor={status}
          aoAlterado={(value) => setStatus(value)}
        />

        <div className="form-group omie-observacao" data-field="observacao">
          <label htmlFor="observacao">Observacao</label>
          <textarea
            id="observacao"
            name="observacao"
            rows={3}
            maxLength={1000}
            placeholder="Anotacoes internas sobre o pedido"
            value={observacao}
            onChange={(event) => setObservacao(event.target.value)}
          />
          <div className="helper">Campo opcional para anotacoes internas.</div>
        </div>

        {commissionItems.length > 0 && (
          <div className="omie-products-section">
            <div className="omie-products-header">
              <div className="omie-products-title">Produtos do pedido</div>
              <div className="omie-products-subtitle">
                Defina o percentual de comissao por produto. O total sera somado automaticamente.
              </div>
            </div>

            <div className="omie-products-list">
              {commissionItems.map((item) => {
                const pct = resolvePercent(item.commission_percent, toFiniteNumber(commission_percent));
                const itemCommission =
                  pct === null || pct < 0 ? 0 : calcCommissionValue(item.item_total, pct);

                return (
                  <div className="omie-product-row" key={item.row_key}>
                    <div className="omie-product-info">
                      <div className="omie-product-name">{item.description}</div>
                      <div className="omie-product-meta">
                        Qtd: {item.quantity} | Unitario: {formatCurrencyBRL(item.unit_price)} | Total:{" "}
                        {formatCurrencyBRL(item.item_total)}
                      </div>
                    </div>

                    <div className="omie-product-percent">
                      <label htmlFor={`pct_${item.row_key}`}>Comissao (%)</label>
                      <input
                        id={`pct_${item.row_key}`}
                        type="number"
                        min="0"
                        step="0.01"
                        placeholder="%"
                        value={item.commission_percent}
                        onChange={(event) =>
                          alterarPercentualItem(item.row_key, event.target.value)
                        }
                      />
                      <div className="omie-product-preview">
                        Previsto: {formatCurrencyBRL(itemCommission)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        <div className="form-actions">
          <button
            type="submit"
            className={`btn-primary ${loading ? "is-disabled" : ""}`}
            disabled={loading}
            title="Confirma e gera lancamento no extrato do licenciado"
          >
            <span>Confirmar e gerar extrato</span>
          </button>
        </div>
      </form>
    </section>
  );
};

export default FormularioPedidoOmie;
